// Anexar imagem ou arquivo (0.70, texto 09 C1/C2): UM caminho para todas as telas.
// O menu (components/MenuAnexo) oferece "Fotos" (galeria), "Tirar foto" e "Arquivos"; aqui fica o que cada um faz.
//
// - Fotos: galeria do celular (expo-image-picker). Esse módulo nativo só existe a partir do app novo (build 0.70).
//   No app instalado antes disso ele NÃO existe: carregar o pacote quebraria o app, por isso só é carregado
//   depois de conferir que o nativo está lá (temGaleria). Sem ele, o menu avisa e oferece "Arquivos".
// - Tirar foto: câmera do sistema (expo-image-picker) no app novo; no app antigo, a câmera do próprio app
//   (CameraTela), mostrada por cima da folha aberta pelo menu.
// - Arquivos: o seletor de arquivos de sempre (lib/anexos: Drive, OneDrive, Dropbox; atalho no Android).
//
// Toda janela do sistema vai dentro de semBloqueio (lib/ausencia): na volta, o app não pede a senha,
// o Diário continua aberto e nada conta como "saiu do app".
// Uma escolha por vez: um segundo toque enquanto o seletor está aberto é ignorado (no iPhone, um segundo
// seletor aberto por cima do primeiro deixava o seletor "preso" até fechar o app).
import { Platform } from 'react-native';
import type { Anexo } from '../store/types';
import { escolherArquivo, escolherVarios, guardarEscolhido, guardarFoto } from './anexos';
import { destinoAtual, guardarSeTiverNuvem, semCopiaLocal } from './nuvem';
import { semBloqueio } from './ausencia';
import { t } from './i18n';

/** imagem = só imagens; arquivo = qualquer arquivo; tudo = qualquer arquivo, com Fotos e câmera em destaque. */
export type TipoAnexo = 'imagem' | 'arquivo' | 'tudo';
export type OrigemAnexo = 'fotos' | 'camera' | 'arquivos';
/** O que volta da escolha. anexo = pronto para guardar no item (com o link do Drive/OneDrive, se houver). */
export type AnexoEscolhido = { uri: string; nome: string; tipo: string; tamanho?: number; anexo: Anexo; aviso?: string;
  /** Com varios: as outras fotos/vídeos escolhidos de uma vez (o primeiro é "anexo"). */
  outros?: Anexo[] };

// ─── galeria (expo-image-picker), só se o módulo nativo existir neste app ───
type ImagePickerJs = typeof import('expo-image-picker');
let galeria: ImagePickerJs | null | undefined;
function picker(): ImagePickerJs | null {
  if (galeria !== undefined) return galeria;
  galeria = null;
  if (Platform.OS === 'web') return galeria;
  try {
    const nativo = require('expo-modules-core').requireOptionalNativeModule('ExponentImagePicker');
    if (nativo) galeria = require('expo-image-picker') as ImagePickerJs;
  } catch { galeria = null; }
  return galeria;
}
/** A galeria de Fotos está neste app? (false no app instalado antes da 0.70 e no navegador) */
export const temGaleria = () => !!picker();
/** A câmera do sistema está neste app? Sem ela, o menu usa a câmera do próprio app. */
export const temCameraSistema = () => !!picker();

let ocupado = false;
/** Algum seletor do sistema está aberto agora? */
export const escolhendo = () => ocupado;

const espera = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/** Foto da galeria ou da câmera do sistema: copia para a pasta do My Planner.
 *  varios: na galeria, várias fotos/vídeos de uma vez (até 30). */
async function daGaleria(origem: 'fotos' | 'camera', tipo: TipoAnexo, varios = false, frente = false): Promise<Anexo[]> {
  const IP = picker();
  if (!IP) return [];
  if (origem === 'camera') {
    const p = await semBloqueio(() => IP.requestCameraPermissionsAsync());
    if (!p.granted) throw new Error(t('Libere a câmera para o My Planner nas configurações do celular.'));
    const r = await semBloqueio(() => IP.launchCameraAsync({ mediaTypes: ['images'], quality: 0.7, exif: false, ...(frente ? { cameraType: IP.CameraType.front } : {}) }));
    if (r.canceled || !r.assets?.length) return [];
    return [await guardarFoto(r.assets[0].uri)];
  }
  const r = await semBloqueio(() => IP.launchImageLibraryAsync({
    mediaTypes: tipo === 'imagem' ? ['images'] : ['images', 'videos'], quality: 0.85, exif: false,
    allowsMultipleSelection: varios, ...(varios ? { selectionLimit: 30 } : {}),
  }));
  if (r.canceled || !r.assets?.length) return [];
  const out: Anexo[] = [];
  for (const [i, a] of r.assets.slice(0, varios ? 30 : 1).entries()) {
    const video = a.type === 'video';
    const mime = a.mimeType || (video ? 'video/mp4' : 'image/jpeg');
    const ext = (mime.split('/')[1] || 'jpg').replace('jpeg', 'jpg').replace('quicktime', 'mov');
    const nome = a.fileName || `${video ? 'video' : 'foto'}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}${r.assets.length > 1 ? '-' + (i + 1) : ''}.${ext}`;
    out.push(await guardarEscolhido({ uri: a.uri, name: nome, mimeType: mime, size: a.fileSize ?? null }));
  }
  return out;
}

/** Escolhe e guarda. Devolve null se a pessoa cancelar (ou se já houver um seletor aberto).
 *  cameraDoApp: a câmera do próprio app, usada em "Tirar foto" quando não há câmera do sistema (o menu passa).
 *  esperarMs: espera antes de abrir a janela do sistema (para a janela anterior terminar de fechar).
 *  varios: em "Fotos" (e em "Arquivos" quando tipo = imagem), várias de uma vez (as demais vêm em "outros").
 *  frente: "Tirar foto" na câmera da frente. */
export async function escolherAnexo(o: { tipo?: TipoAnexo; origem: OrigemAnexo; semNuvem?: boolean; esperarMs?: number; varios?: boolean; frente?: boolean;
  cameraDoApp?: () => Promise<Anexo | null> }): Promise<AnexoEscolhido | null> {
  if (ocupado) return null;
  ocupado = true;
  try {
    const tipo = o.tipo ?? 'tudo';
    if (o.esperarMs) await espera(o.esperarMs);
    const destino = o.semNuvem ? null : destinoAtual();
    let as: Anexo[];
    if (o.varios && (o.origem === 'fotos' ? !temGaleria() : o.origem === 'arquivos' && tipo === 'imagem')) as = await semBloqueio(() => escolherVarios(tipo === 'imagem'));
    else if (o.origem === 'arquivos' || (o.origem === 'fotos' && !temGaleria())) {
      const a = await semBloqueio(() => escolherArquivo(tipo === 'imagem' || o.origem === 'fotos', !!destino));
      as = a ? [a] : [];
    } else if (o.origem === 'camera' && !temCameraSistema()) {
      const a = o.cameraDoApp ? await o.cameraDoApp() : null;
      as = a ? [a] : [];
    } else as = await daGaleria(o.origem, tipo, !!o.varios, !!o.frente);
    if (!as.length) return null;
    // Drive/OneDrive (se estiver ligado em Ajustes); atalhos do Android ficam onde estão
    const prontos: Anexo[] = [];
    let aviso: string | undefined;
    for (const a of as) {
      if (destino && !a.atalho) {
        const r = await guardarSeTiverNuvem(a, false);
        prontos.push(semCopiaLocal(r.arq)); aviso = aviso ?? r.aviso;
      } else prontos.push(a);
    }
    const [anexo, ...outros] = prontos;
    return { uri: anexo.uri ?? anexo.url ?? anexo.dado ?? '', nome: anexo.nome, tipo: anexo.mime ?? '', tamanho: anexo.tamanho, anexo, aviso,
      ...(outros.length ? { outros } : {}) };
  } finally { ocupado = false; }
}
